import { entryFor, foldSurface, idForSurface } from './ingredientVocabulary';

type PantryName = {
  name: string;
  names?: { en: string; fr: string };
  source?: string;
};

/** "carottes" of "carotte", "sweet potatoes" of "sweet potato", word by word. */
function isPluralOf(plural: string, singular: string): boolean {
  const many = foldSurface(plural).split(' ');
  const one = foldSurface(singular).split(' ');
  return (
    many.length === one.length &&
    many.some((word, i) => word !== one[i]) &&
    many.every(
      (word, i) =>
        word === one[i] ||
        [`${one[i]}s`, `${one[i]}x`, `${one[i]}es`, one[i].replace(/y$/, 'ies')].includes(word)
    )
  );
}

function vocabularyName(name: string, language: 'en' | 'fr'): string | null {
  const entry = entryFor(idForSurface(name));
  if (!entry) return null;

  const key = foldSurface(name);
  const other = language === 'fr' ? 'en' : 'fr';
  const inForms = (forms: string[]) =>
    forms.some(form => {
      const surface = foldSurface(form);
      return key === surface || key === `${surface}s` || `${key}s` === surface;
    });
  if (inForms(entry[language])) return null;

  // Only the word and its plural. The other forms are aliases, some of them a
  // narrower thing: jalapeños live under `chili-pepper`, and reading them
  // « Piment » would be a wrong name, where leaving them is only an English one.
  const word = entry[other].filter((form, i) => i === 0 || isPluralOf(form, entry[other][0]));
  if (!inForms(word)) return null;

  // Folding drops accents, so « pâté » is the same key as « pâte », which is
  // dough. An accent that is not the vocabulary's says it is another word.
  const lower = name.toLowerCase();
  const typedAsListed = word.some(form => lower === form || lower === `${form}s`);
  if (!typedAsListed && /[^\x00-\x7F]/.test(name)) return null;

  const [singular, ...forms] = entry[language];
  const plural = /[sx]$/.test(key) && key !== foldSurface(entry[other][0]);
  const form = plural ? (forms.find(f => isPluralOf(f, singular)) ?? singular) : singular;
  return form.charAt(0).toLocaleUpperCase(language) + form.slice(1);
}

/**
 * Units a delivery writes in English (`UNIT_TRANSLATIONS` in lufaDeliveryParse).
 * Stored that way because quantities are compared and merged on them, so like
 * the name they are translated only on the way to the screen.
 */
const UNIT_NAMES: Record<string, Record<'en' | 'fr', { one: string; other: string }>> = {
  bag: { en: { one: 'bag', other: 'bags' }, fr: { one: 'sac', other: 'sacs' } },
  bunch: { en: { one: 'bunch', other: 'bunches' }, fr: { one: 'botte', other: 'bottes' } },
  punnet: { en: { one: 'punnet', other: 'punnets' }, fr: { one: 'casseau', other: 'casseaux' } },
  jar: { en: { one: 'jar', other: 'jars' }, fr: { one: 'pot', other: 'pots' } },
  box: { en: { one: 'box', other: 'boxes' }, fr: { one: 'boîte', other: 'boîtes' } },
  pack: { en: { one: 'pack', other: 'packs' }, fr: { one: 'paquet', other: 'paquets' } },
  head: { en: { one: 'head', other: 'heads' }, fr: { one: 'tête', other: 'têtes' } },
  slice: { en: { one: 'slice', other: 'slices' }, fr: { one: 'tranche', other: 'tranches' } },
  dozen: { en: { one: 'dozen', other: 'dozen' }, fr: { one: 'douzaine', other: 'douzaines' } },
};

/** "bag" as a cook reads it beside a quantity: « 2 sacs ». A unit it does not know is left as stored. */
export function pantryUnitName(unit: string, language: string, quantity: number = 1): string {
  const reader = language.toLowerCase().startsWith('fr') ? 'fr' : 'en';
  const entry = UNIT_NAMES[unit.trim().toLowerCase()]?.[reader];
  if (!entry) return unit;
  return new Intl.PluralRules(reader).select(quantity) === 'one' ? entry.one : entry.other;
}

/**
 * The name a cook reads for a pantry row. `name` is the row's one stored name,
 * in whatever language it arrived: a receipt in the app's language of that day,
 * a recipe line in the recipe's, a delivery in the import's. It is what matching
 * and duplicate detection read, so it is never rewritten; this is display only.
 *
 * Only a row that is exactly a food the vocabulary knows is translated. "Thon
 * citron poivre" holds a food word and is a can of tuna, so a product keeps the
 * name it came with. `names` is valid only while `name` is one of the pair: a
 * rename or a merge replaces `name`, and the pair then says nothing about the row.
 */
export function pantryDisplayName(item: PantryName, language: string): string {
  if (item.source === 'leftover') return item.name;

  const reader = language.toLowerCase().startsWith('fr') ? 'fr' : 'en';
  const { name, names } = item;
  if (names && (names.en === name || names.fr === name)) return names[reader];
  return vocabularyName(name.trim(), reader) ?? name;
}
